<template>
  <div class="ai-agent-create-page">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <v-btn
                icon
                color="primary"
                @click="$router.back()"
                class="back-btn mr-3"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-robot-excited</v-icon>
                Edit AI Agent
                <span class="page-inline-subtitle">Update AI agent information for the Vijan.AI marketplace</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <!-- Header actions removed - button moved to bottom -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Agent Form Card -->
      <v-card class="agent-form-card" elevation="8">
        <v-card-title class="modern-card-title">
          <h2 class="card-title">
            <v-icon class="mr-2">mdi-form-select</v-icon>
            AI Agent Information
          </h2>
        </v-card-title>
        <v-card-text class="pa-4">
          <!-- Basic Information Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-information-outline</v-icon>
              Basic Information
            </h3>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Agent Name *"
                  prepend-inner-icon="mdi-robot"
                  :error-messages="errors.name"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.version"
                  outlined
                  label="Version"
                  prepend-inner-icon="mdi-tag"
                  :error-messages="errors.version"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                  v-model="form.industry_id"
                  :items="industries"
                  outlined
                  label="Industry *"
                  prepend-inner-icon="mdi-domain"
                  :error-messages="errors.industry_id"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.description"
                  outlined
                  label="Description *"
                  prepend-inner-icon="mdi-text"
                  :error-messages="errors.description"
                  class="minimal-input"
                  dense
                  rows="3"
                  hide-details="auto"
                ></v-textarea>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Pricing Information Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-currency-usd</v-icon>
              Pricing Information
            </h3>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.monthly_price"
                  outlined
                  label="Monthly Price *"
                  prepend-inner-icon="mdi-currency-usd"
                  :error-messages="errors.monthly_price"
                  class="minimal-input"
                  dense
                  type="number"
                  min="0"
                  step="0.01"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.annual_price"
                  outlined
                  label="Annual Price *"
                  prepend-inner-icon="mdi-currency-usd"
                  :error-messages="errors.annual_price"
                  class="minimal-input"
                  dense
                  type="number"
                  min="0"
                  step="0.01"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.setup_fee"
                  outlined
                  label="Setup Fee"
                  prepend-inner-icon="mdi-cash"
                  :error-messages="errors.setup_fee"
                  class="minimal-input"
                  dense
                  type="number"
                  min="0"
                  step="0.01"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-switch
                  v-model="form.is_active"
                  inset
                  color="primary"
                  :label="`Status: ${form.is_active ? 'Active' : 'Inactive'}`"
                  class="minimal-switch mt-2"
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Logo Upload Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-image</v-icon>
              Agent Logo
            </h3>
            <v-row>
              <v-col cols="12" md="6">
                <div class="logo-upload-section">
                  <v-file-input
                    ref="file"
                    v-model="logoFile"
                    label="Upload Agent Logo"
                    prepend-icon="mdi-camera"
                    accept="image/*"
                    outlined
                    dense
                    class="minimal-input"
                    @change="onFileChange"
                    hide-details="auto"
                  ></v-file-input>
                  
                  <div v-if="image_url" class="logo-preview mt-4">
                    <div class="preview-header mb-2">
                      <span class="preview-label">Logo Preview</span>
                      <v-btn
                        x-small
                        color="error"
                        outlined
                        @click="removeImage"
                        class="ml-2"
                      >
                        <v-icon small left>mdi-close</v-icon>
                        Remove
                      </v-btn>
                    </div>
                    <div class="preview-container">
                      <img :src="image_url" alt="Agent Logo" class="logo-preview-image" />
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Features Section -->
          <div class="form-section mb-6">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-star</v-icon>
              Features
            </h3>
            <v-row>
              <v-col cols="12">
                <div class="features-section">
                  <div v-for="(feature, index) in form.features" :key="index" class="feature-item mb-3">
                    <v-row>
                      <v-col cols="12" sm="10">
                        <v-text-field
                          v-model="feature.name"
                          outlined
                          :label="`Feature ${index + 1} *`"
                          prepend-inner-icon="mdi-check"
                          class="minimal-input"
                          dense
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="2" class="d-flex align-center">
                        <v-btn
                          v-if="form.features.length > 1"
                          icon
                          color="error"
                          @click="removeFeature(index)"
                        >
                          <v-icon>mdi-minus</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="index === form.features.length - 1"
                          icon
                          color="primary"
                          @click="addFeature"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-5"></v-divider>

          <!-- Configuration Section -->
          <div class="form-section">
            <h3 class="section-title mb-4">
              <v-icon class="mr-2">mdi-cog</v-icon>
              Configuration
            </h3>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.api_endpoint"
                  outlined
                  label="API Endpoint"
                  prepend-inner-icon="mdi-link"
                  :error-messages="errors.api_endpoint"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="form.webhook_url"
                  outlined
                  label="Webhook URL"
                  prepend-inner-icon="mdi-webhook"
                  :error-messages="errors.webhook_url"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="form.deployment_status"
                  :items="deploymentStatuses"
                  outlined
                  label="Deployment Status"
                  prepend-inner-icon="mdi-cloud"
                  class="minimal-input"
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        
        <!-- Form Actions -->
        <v-card-actions class="minimal-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn 
            text
            @click="$router.back()"
            class="minimal-cancel-btn mr-2"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
            class="minimal-save-btn"
            elevation="1"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Update AI Agent
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AIAgentEditPage",
  data() {
    return {
      form: {
        name: "",
        description: "",
        version: "1.0.0",
        industry_id: null,
        monthly_price: "",
        annual_price: "",
        setup_fee: "",
        api_endpoint: "",
        webhook_url: "",
        deployment_status: "development",
        is_active: true,
        features: [{ name: "" }],
      },
      errors: {},
      isLoading: false,
      image_url: "",
      logoFile: null,
      file: null,
      industries: [],
      deploymentStatuses: [
        { text: "Development", value: "development" },
        { text: "Testing", value: "testing" },
        { text: "Production", value: "production" },
        { text: "Maintenance", value: "maintenance" },
      ],
    };
  },
  mounted() {
    this.getIndustries();
    this.loadAgentData();
  },
  methods: {
    async getIndustries() {
      try {
        const response = await axios.get('/industries');
        this.industries = response.data.data.map(industry => ({
          text: industry.name,
          value: industry.id
        }));
      } catch (error) {
        console.error('Error fetching industries:', error);
        // Fallback to sample industries
        this.industries = [
          { text: 'Marketing', value: 1 },
          { text: 'Technology', value: 2 },
          { text: 'Finance', value: 3 },
          { text: 'Healthcare', value: 4 },
          { text: 'Education', value: 5 },
          { text: 'Retail', value: 6 }
        ];
      }
    },
    async loadAgentData() {
      try {
        const encryptedId = this.$route.params.id;
        
        if (!encryptedId) {
          this.$router.push("/ai-agents");
          return;
        }
        
        this.isLoading = true;
        
        // For demo purposes, always use sample data since backend API is not available
        // Decrypt the ID to determine which agent to load
        const decryptedAgentId = this.decryptIt(encryptedId);
        const agentId = parseInt(decryptedAgentId);
        
        // Sample agent data - in real app this would come from API
        const sampleAgents = {
          1: {
            name: 'ContentMod AI',
            version: '2.1.0',
            industry_id: 1,
            description: 'Advanced AI-powered content moderation platform that automatically detects and filters inappropriate content, spam, and toxicity.',
            monthly_price: 99,
            annual_price: 950,
            setup_fee: 0,
            features: [
              { name: 'Real-time content analysis' },
              { name: 'Toxicity detection' },
              { name: 'Spam filtering' },
              { name: 'Custom moderation rules' },
              { name: 'Multi-language support' }
            ],
            is_active: true
          }
        };
        
        const agent = sampleAgents[agentId];
        if (agent) {
          this.form = {
            name: agent.name,
            version: agent.version,
            industry_id: agent.industry_id,
            description: agent.description,
            monthly_price: agent.monthly_price,
            annual_price: agent.annual_price,
            setup_fee: agent.setup_fee,
            features: agent.features,
            api_endpoint: "",
            webhook_url: "",
            deployment_status: "production",
            is_active: agent.is_active,
          };
        } else {
          alert('AI Agent not found');
          this.$router.push("/ai-agents");
        }
      } catch (error) {
        console.error('Error loading agent data:', error);
        alert('AI Agent not found');
        this.$router.push("/ai-agents");
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};
        
        const agentId = this.$route.params.id;
        if (!agentId) {
          this.$router.push("/ai-agents");
          return;
        }
        
        let formTest = {};
        Object.assign(formTest, this.form);
        let formData = this.jsonToFormData(formTest);
        
        let attachment = this.$refs.file?.files[0];
        if (attachment) {
          formData.append("logo", attachment);
        }
        
        await axios
          .put(`/ai-agents/${agentId}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(async () => {
            this.isLoading = false;
            await this.saveUserTimestamp(null, this.form);
            this.$router.push("/ai-agents");
          })
          .catch((error) => {
            this.isLoading = false;
            if (error.response && error.response.data && error.response.data.errors) {
              this.errors = error.response.data.errors;
            }
            console.log(error);
          });
      } catch (e) {
        this.isLoading = false;
        console.error("Error updating AI agent:", e);
      }
    },
    jsonToFormData(data) {
      const formData = new FormData();
      this.buildFormData(formData, data);
      return formData;
    },
    buildFormData(formData, data, parentKey) {
      if (
        data &&
        typeof data === "object" &&
        !(data instanceof Date) &&
        !(data instanceof File)
      ) {
        Object.keys(data).forEach((key) => {
          this.buildFormData(
            formData,
            data[key],
            parentKey ? `${parentKey}[${key}]` : key
          );
        });
      } else {
        const value = data == null ? "" : data;
        formData.append(parentKey, value);
      }
    },
    onFileChange() {
      this.file = this.$refs.file?.files[0];
      if (this.file) {
        let reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = (evt) => {
          let img = new Image();
          img.onload = () => {
            this.image_url = URL.createObjectURL(this.file);
          };
          img.src = evt.target.result;
        };
        reader.onerror = (evt) => {
          console.error(evt);
        };
      }
    },
    removeImage() {
      this.image_url = "";
      this.logoFile = null;
      this.file = null;
      if (this.$refs.file) {
        this.$refs.file.value = null;
      }
    },
    addFeature() {
      this.form.features.push({ name: "" });
    },
    removeFeature(index) {
      if (this.form.features.length > 1) {
        this.form.features.splice(index, 1);
      }
    },
  },
};
</script>

<style scoped>
/* AI Agent Edit Page */
.ai-agent-create-page {
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.back-btn {
  border-radius: 12px !important;
  background: rgba(102, 126, 234, 0.1) !important;
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(102, 126, 234, 0.15) !important;
  transform: translateY(-1px);
}

/* Agent Form Card */
.agent-form-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15) !important;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  margin: 0 !important;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Form Sections */
.form-section {
  padding: 16px 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  margin: 0;
}

.section-title .v-icon {
  color: #667eea;
  margin-right: 8px;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: #f8f9fa !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> input::placeholder {
  color: #94a3b8 !important;
  font-weight: 400;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

/* Logo Upload Section */
.logo-upload-section {
  padding: 16px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.logo-upload-section:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.logo-preview {
  display: flex;
  flex-direction: column;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.preview-label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.logo-preview-image {
  max-width: 120px;
  max-height: 80px;
  object-fit: contain;
  border-radius: 4px;
}

/* Features Section */
.features-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.feature-item {
  background: white;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
}

/* Form Actions */
.minimal-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  padding: 20px 24px !important;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.minimal-cancel-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 20px !important;
  height: 44px !important;
  color: #64748b !important;
  border: 1.5px solid #e2e8f0 !important;
  background: white !important;
  transition: all 0.3s ease;
}

.minimal-cancel-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
  transform: translateY(-1px);
}

.minimal-save-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 0 24px !important;
  height: 44px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border: none !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

.minimal-save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
}

.minimal-save-btn:before {
  background-color: transparent;
}

/* Dividers */
.v-divider {
  border-color: rgba(102, 126, 234, 0.1) !important;
  margin: 20px 0 !important;
}

/* Error Messages */
.v-messages {
  color: #ef4444 !important;
  font-size: 12px !important;
  font-weight: 500 !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 22px;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .modern-card-title {
    padding: 20px !important;
  }
  
  .card-title {
    font-size: 18px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
  }
  
  .minimal-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .minimal-cancel-btn,
  .minimal-save-btn {
    width: 100%;
  }
  
  .logo-upload-section {
    padding: 12px;
  }
  
  .logo-preview-image {
    max-width: 100px;
    max-height: 60px;
  }
}
</style>